import requestUrlInfo from 'js/lib/requestUrlInfo';
import { useLocation } from 'js/lib/useRouter';
import { isAuthenticatedUser } from 'js/lib/user';

import { redirectClientOrServer } from 'bundles/common/utils/urlUtils';
import inServerContext from 'bundles/ssr/util/inServerContext';
import { useUserExperienceContext } from 'bundles/unified-common/contexts/UserExperienceContext';
import { useIsOrgScopedHome } from 'bundles/unified-common/hooks/useIsOrgScopedHome';
import type { MembershipsForRedirect } from 'bundles/unified-common/utils/userExperienceRedirectUtils';
import {
  SelectionAction,
  getUserExperienceRedirectOrSetSelection,
} from 'bundles/unified-common/utils/userExperienceRedirectUtils';

const normalizePathname = (p: string) => (p === '' ? '/' : p);

type Props = {
  memberships: MembershipsForRedirect | null;
  isMembershipsLoading: boolean;
  isAdminPreviewingRouteOrg?: boolean;
};

/**
 * Server-only branch for HTTP redirects (same pattern as `AdminRootRedirect` / `EnterprisePageGuard`:
 * after data is ready, call `redirectClientOrServer` during render — on SSR it throws `RedirectError`).
 *
 * Runs during the markup pass after `loadDataFromTree` when Apollo has cached UX + membership queries.
 * `SelectionAction.SET_SELECTION` is not handled here (mutations run on the client in `useUserExperienceRedirect`).
 */
const UserExperienceSsrRedirect = ({ memberships, isMembershipsLoading, isAdminPreviewingRouteOrg = false }: Props) => {
  const { pathname: routerPathname, search: routerSearch } = useLocation();
  const {
    userExperience,
    isLoading: isUserExperienceLoading,
    isUpdating: isUserExperienceUpdating,
    error: userExperienceError,
    querySkipped: userExperienceQuerySkipped,
  } = useUserExperienceContext();
  const shouldRunRedirectLogic = useIsOrgScopedHome();

  // When the UX context query errored and returned no data, the context is unknown (not
  // "unset"). Redirecting on unknown context sends users to the wrong org (LRNRSCCSS-6393).
  // Only guard when data is truly missing — if Apollo returned partial data alongside errors
  // (errorPolicy: 'all'), we still use the available selection.
  const isUserExperienceUnknown = !!userExperienceError && !userExperience;

  if (!inServerContext) {
    return null;
  }
  if (
    !shouldRunRedirectLogic ||
    !isAuthenticatedUser() ||
    isUserExperienceLoading ||
    isUserExperienceUpdating ||
    isMembershipsLoading ||
    isUserExperienceUnknown ||
    userExperienceQuerySkipped
  ) {
    return null;
  }

  const pathname = normalizePathname(routerPathname);
  const pathnameForRedirect = pathname;
  const search = routerSearch || '';
  const host = requestUrlInfo().hostname;

  const result = getUserExperienceRedirectOrSetSelection(
    pathname,
    pathnameForRedirect,
    search,
    host,
    userExperience,
    memberships,
    isAdminPreviewingRouteOrg
  );

  if (result?.action === SelectionAction.REDIRECT) {
    redirectClientOrServer(result.url);
  }

  return null;
};

export default UserExperienceSsrRedirect;
